export const DEFAULT_TASK_BOARD_COLUMNS = [
  { id: 'to-do', name: 'To Do', cards: [] },
  { id: 'in-progress', name: 'In Progress', cards: [] },
  { id: 'done', name: 'Done', cards: [] },
]

const asArray = (value) => (Array.isArray(value) ? value : [])

const normalizeMember = (member) => ({
  userId: String(member?.userId || member?.TeacherID || member?._id || member?.id || '').trim(),
  name: String(member?.name || member?.Name || '').trim(),
  imageName: String(member?.imageName || member?.ImageName || '').trim(),
})

const normalizeChecklist = (checklist) => ({
  id: String(checklist?.id || '').trim(),
  title: String(checklist?.title || 'Checklist').trim() || 'Checklist',
  hideCompleted: checklist?.hideCompleted === true,
  items: asArray(checklist?.items).map((item) => ({
    id: String(item?.id || '').trim(),
    text: String(item?.text || '').trim(),
    completed: item?.completed === true,
  })).filter((item) => item.id && item.text),
})

const normalizeDueDate = (value) => {
  if (!value) return null
  const dueDate = new Date(value)
  return Number.isNaN(dueDate.getTime()) ? null : dueDate.toISOString()
}

const normalizeActivity = (activity) => {
  const occurredAt = new Date(activity?.occurredAt)
  if (activity?.type !== 'column-change' || Number.isNaN(occurredAt.getTime())) return null
  const id = String(activity?.id || '').trim()
  const fromColumnName = String(activity?.fromColumnName || '').trim()
  const toColumnName = String(activity?.toColumnName || '').trim()
  if (!id || !fromColumnName || !toColumnName) return null
  return {
    id,
    type: 'column-change',
    fromColumnId: String(activity?.fromColumnId || '').trim(),
    fromColumnName,
    toColumnId: String(activity?.toColumnId || '').trim(),
    toColumnName,
    actorId: String(activity?.actorId || '').trim(),
    actorName: String(activity?.actorName || 'Administrator').trim() || 'Administrator',
    occurredAt: occurredAt.toISOString(),
  }
}

export const normalizeTaskBoard = (board) => {
  const sourceColumns = asArray(board?.columns).length > 0
    ? board.columns
    : DEFAULT_TASK_BOARD_COLUMNS

  return {
    ...board,
    name: board?.name || 'Atharo Task Board',
    revision: Number.isInteger(board?.revision) ? board.revision : 0,
    columns: sourceColumns.map((column) => {
      return {
        id: String(column?.id || '').trim(),
        name: String(column?.name || '').trim(),
        cards: asArray(column?.cards).map((card) => ({
          ...card,
          id: String(card?.id || ''),
          title: String(card?.title || ''),
          description: String(card?.description || ''),
          dueDate: normalizeDueDate(card?.dueDate),
          assignedMembers: asArray(card?.assignedMembers)
            .map(normalizeMember)
            .filter((member) => member.userId && member.name),
          checklists: asArray(card?.checklists)
            .map(normalizeChecklist)
            .filter((checklist) => checklist.id),
          activity: asArray(card?.activity)
            .map(normalizeActivity)
            .filter(Boolean),
        })).filter((card) => card.id && card.title),
      }
    }).filter((column) => column.id && column.name),
  }
}

export const getTaskDueDateStatus = (value, now = new Date()) => {
  if (!value) return null
  const dueDate = new Date(value)
  const currentDate = new Date(now)
  if (Number.isNaN(dueDate.getTime()) || Number.isNaN(currentDate.getTime())) return null

  const millisecondsRemaining = dueDate.getTime() - currentDate.getTime()
  return {
    dueDate,
    status: millisecondsRemaining < 0
      ? 'overdue'
      : millisecondsRemaining <= 24 * 60 * 60 * 1000
        ? 'due-soon'
        : 'upcoming',
  }
}

export const getTaskChecklistProgress = (card) => {
  const checklists = asArray(card?.checklists)
  const items = checklists.reduce(
    (allItems, checklist) => [...allItems, ...asArray(checklist?.items)],
    []
  )
  const completed = items.filter((item) => item?.completed === true).length
  const total = items.length

  return {
    checklistCount: checklists.length,
    completed,
    total,
    percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
  }
}

export const getAssignableTaskMembers = (people, currentUser = null) => {
  const candidates = [
    ...asArray(people).filter((person) => person?.IsAdmin === true),
    ...(currentUser?.IsAdmin === true ? [currentUser] : []),
  ]
  const membersById = new Map()

  candidates.forEach((person) => {
    const member = normalizeMember(person)
    if (member.userId && member.name) {
      const existingMember = membersById.get(member.userId)
      membersById.set(member.userId, {
        ...existingMember,
        ...member,
        imageName: member.imageName || existingMember?.imageName || '',
      })
    }
  })

  return [...membersById.values()].sort((memberA, memberB) => (
    memberA.name.localeCompare(memberB.name, undefined, { sensitivity: 'base' })
  ))
}

export const getTaskMemberInitials = (name) => String(name || 'Admin')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((namePart) => namePart[0])
  .join('')
  .toUpperCase()

export const resolveTaskMemberImageUrl = (imageName) => {
  const normalized = String(imageName || '').trim()
  if (!normalized) return ''
  if (/^(https?:|data:|blob:)/i.test(normalized)) return normalized
  return `https://app.atharo.app/${normalized.replace(/^\/+/, '')}`
}

export const createTaskCardId = () => {
  if (typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function') {
    return window.crypto.randomUUID()
  }
  return `task-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

export const addTaskColumn = (columns, column) => [
  ...asArray(columns).map((currentColumn) => ({
    ...currentColumn,
    cards: [...asArray(currentColumn.cards)],
  })),
  { ...column, cards: [] },
]

export const renameTaskColumn = (columns, columnId, name) => asArray(columns).map((column) => (
  column.id === columnId ? { ...column, name } : { ...column }
))

export const moveTaskColumn = (columns, columnId, targetColumnId) => {
  if (!columnId || !targetColumnId || columnId === targetColumnId) return columns

  const nextColumns = [...asArray(columns)]
  const sourceIndex = nextColumns.findIndex((column) => column.id === columnId)
  const targetIndex = nextColumns.findIndex((column) => column.id === targetColumnId)
  if (sourceIndex < 0 || targetIndex < 0) return columns

  const [movedColumn] = nextColumns.splice(sourceIndex, 1)
  nextColumns.splice(targetIndex, 0, movedColumn)
  return nextColumns
}

export const addTaskCard = (columns, columnId, card) => asArray(columns).map((column) => (
  column.id === columnId
    ? { ...column, cards: [...asArray(column.cards), card] }
    : { ...column, cards: [...asArray(column.cards)] }
))

export const updateTaskCard = (columns, cardId, updates) => asArray(columns).map((column) => ({
  ...column,
  cards: asArray(column.cards).map((card) => (
    card.id === cardId ? { ...card, ...updates } : card
  )),
}))

export const removeTaskCard = (columns, cardId) => asArray(columns).map((column) => ({
  ...column,
  cards: asArray(column.cards).filter((card) => card.id !== cardId),
}))

export const moveTaskCard = (columns, cardId, targetColumnId, targetCardId = null) => {
  if (!cardId || !targetColumnId || cardId === targetCardId) return columns

  const nextColumns = asArray(columns).map((column) => ({
    ...column,
    cards: [...asArray(column.cards)],
  }))
  const sourceColumn = nextColumns.find((column) => (
    column.cards.some((card) => card.id === cardId)
  ))
  const targetColumn = nextColumns.find((column) => column.id === targetColumnId)

  if (!sourceColumn || !targetColumn) return columns

  const sourceIndex = sourceColumn.cards.findIndex((card) => card.id === cardId)
  const [movedCard] = sourceColumn.cards.splice(sourceIndex, 1)
  const targetIndex = targetCardId
    ? targetColumn.cards.findIndex((card) => card.id === targetCardId)
    : -1

  targetColumn.cards.splice(targetIndex >= 0 ? targetIndex : targetColumn.cards.length, 0, movedCard)
  return nextColumns
}
